import React, { useMemo } from 'react'
import { ThemeProvider } from 'styled-components'

import {
  Badge as LMFBadge,
  b2bTheme,
  defaultLightTheme,
  isUppercase,
  useLmfAnalyticsOnClick,
} from '@syconium/little-miss-figgy'

import { CtaBadgeRow } from '../../../app/_components/chrome/PrimaryNavAccount.client'
import { NextLink } from '../../../app/_components/navigation/NextLink'
import { requiresFullPageLoad } from '../../../lib/adapters/next-routing-service/utils'
import { NavListItem as Props } from '../../../types/graphql/site-fixtures'

import {
  Badge,
  Body,
  Btn,
  CtaHeadingText,
  FooterItemIcon,
  FooterItemIconWrapper,
  FooterText,
  HeadingText,
  Icon,
  IconWrap,
  RegularWeightHeadingText,
  Text,
  TextWithTrailingIcon,
  TextWrap,
} from './styles'

const PRIVACY_DIALOG_TOGGLE_HREFS = [
  'javascript:Optanon.ToggleInfoDisplay()',
  'javascript:Optanon.ToggleInfoDisplay();',
]

type OptanonWindow = Window & {
  Optanon?: {
    ToggleInfoDisplay: () => void
  }
}

export const NavListItem: React.FC<Props> = ({
  asTag = 'div',
  role,
  text,
  textColor,
  href,
  isHeading,
  isBold,
  badge,
  badgeStyle,
  badgeColor,
  badgeTextColor,
  badgeColorVariant,
  icon,
  textRole = 'listitem',
  onClickish,
  isInFooter,
  htmlId,
  htmlClass,
  isBtn,
  trailingIcon,
  isB2B,
  render,
}) => {
  const isTextBold = isBold || isHeading
  const isFooterVariant = isInFooter
  const isSecondaryText = !href && !isHeading

  const isOneTrustToggle = PRIVACY_DIALOG_TOGGLE_HREFS.includes(href ?? '')

  const onClick: (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void = event => {
    if (isOneTrustToggle) {
      event.preventDefault()
      const w = window as OptanonWindow
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      w?.Optanon?.ToggleInfoDisplay?.() // One trust modal script
      return
    }
    if (!href) event.preventDefault()
    if (onClickish) onClickish(event)
  }

  const onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void = event => {
    if (event.key === 'Enter' && onClickish) onClick(event)
  }

  const shouldRenderHeader = isTextBold && text !== '3XL-6XL'
  const isTextUppercase = isUppercase(text)
  const isTextEmpty = text === ''

  const textTsx = useMemo(() => {
    if (isTextEmpty) {
      return null
    }

    if (isFooterVariant) {
      return (
        <FooterText
          as={isHeading && !(href || onClickish) ? 'section' : Text}
          $isSecondaryText={isSecondaryText}
          $isHeading={isHeading}
        >
          {text}
        </FooterText>
      )
    } else if (shouldRenderHeader) {
      return <HeadingText>{text}</HeadingText>
    } else if (isTextUppercase) {
      return isB2B ? (
        <CtaHeadingText>{text}</CtaHeadingText>
      ) : (
        <RegularWeightHeadingText>{text}</RegularWeightHeadingText>
      )
    }

    return <Text>{text}</Text>
  }, [
    isFooterVariant,
    isHeading,
    href,
    onClickish,
    text,
    shouldRenderHeader,
    isTextUppercase,
    isSecondaryText,
    isTextEmpty,
    isB2B,
  ])

  const normalizedHref = isOneTrustToggle ? '#' : href ?? '#'

  const onClickWithTracking = useLmfAnalyticsOnClick<typeof onClick>({
    element: 'link',
    variations: ['primary', 'underline-on-hover'],
    label: undefined,
    href: normalizedHref,
    onClick,
  })

  const LinkComponent = requiresFullPageLoad(normalizedHref) ? 'a' : NextLink

  const isB2BRow = Boolean(isB2B && !href && !onClickish)
  const textContent = trailingIcon ? (
    <TextWithTrailingIcon>
      {textTsx}
      {trailingIcon}
    </TextWithTrailingIcon>
  ) : (
    textTsx
  )

  const linkOrText = render ? (
    render(textContent)
  ) : isBtn ? (
    <Btn
      $isBold={isTextBold}
      $isUppercase={isUppercase(text)}
      $isSecondaryText={isSecondaryText}
      $isHeading={isHeading}
      id={htmlId ?? undefined}
      className={htmlClass ?? undefined}
      role={textRole}
    >
      {textContent}
    </Btn>
  ) : href || onClickish ? (
    <LinkComponent
      onClick={onClickWithTracking}
      onKeyDown={onKeyDown}
      href={normalizedHref}
      tabIndex={onClickish && !href ? 0 : undefined}
      id={htmlId ?? undefined}
      className={htmlClass ?? undefined}
      role={textRole}
    >
      {isFooterVariant && icon ? (
        <ThemeProvider theme={defaultLightTheme}>
          <FooterItemIconWrapper style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {textContent}
            <FooterItemIcon
              alt='footer nav icon'
              src={icon}
              aspectRatios={null}
              widths={{ unit: 'px', sm: 25, md: 25 }}
              loading='lazy'
              role='presentation'
              transparentBackground={true}
            />
          </FooterItemIconWrapper>
        </ThemeProvider>
      ) : (
        textContent
      )}
    </LinkComponent>
  ) : (
    textContent
  )

  const badgeTsx = badge ? (
    badgeStyle === 'default' ? (
      <LMFBadge
        variant='pill'
        size='small'
        colorVariant={badgeColorVariant ?? (isB2B ? 'info' : 'default')}
      >
        {badge}
      </LMFBadge>
    ) : (
      <Badge $badgeColor={badgeColor} $badgeTextColor={badgeTextColor} $isB2B={Boolean(isB2B)}>
        {badge}
      </Badge>
    )
  ) : null

  const textWrapContent =
    isB2B && badge ? (
      <CtaBadgeRow>
        {linkOrText}
        {badgeTsx}
      </CtaBadgeRow>
    ) : (
      <>
        {linkOrText}
        {badgeTsx}
      </>
    )

  const navItemContent = (
    <Body
      as={asTag}
      role={role}
      $isFooterVariant={isFooterVariant === true}
      $isHeading={isHeading}
      $isSecondaryText={isSecondaryText}
    >
      {icon && !isFooterVariant && (
        <IconWrap>
          <Icon
            alt='nav icon'
            src={icon}
            aspectRatios={{ sm: 1, md: 1 }}
            widths={{ unit: 'px', sm: 16, md: 16 }}
            role='presentation'
            transparentBackground={true}
            loading='lazy'
          />
        </IconWrap>
      )}

      <TextWrap role='none' textColor={textColor} $isB2B={isB2BRow}>
        {textWrapContent}
      </TextWrap>
    </Body>
  )

  return isB2B ? <ThemeProvider theme={b2bTheme}>{navItemContent}</ThemeProvider> : navItemContent
}
